import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Compass, Download, FilePlus2, Loader2, Printer, Search } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loading, PageHeader, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

const Body = ({ section }) => (
  <div className="space-y-3 px-4 py-3.5">
    {(section.p || []).map((t) => (
      <p key={t.slice(0, 24)} className="text-[12.5px] leading-relaxed text-foreground/80">{t}</p>
    ))}
    {section.steps && (
      <ol className="space-y-1.5">
        {section.steps.map((s, i) => (
          <li key={s.slice(0, 24)} className="flex gap-2.5 text-[12.5px] leading-relaxed text-foreground/80">
            <span className="mt-[3px] grid h-4 w-4 shrink-0 place-items-center rounded-full bg-primary/15 font-mono text-[9px] font-bold text-primary">
              {i + 1}
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
    )}
    {section.list && (
      <dl className="divide-y divide-border rounded-lg border border-border">
        {section.list.map(([term, def]) => (
          <div key={term} className="grid gap-1 px-3 py-2 sm:grid-cols-[190px_1fr] sm:gap-4">
            <dt className="text-[12px] font-semibold text-foreground">{term}</dt>
            <dd className="text-[12.5px] leading-relaxed text-muted-foreground">{def}</dd>
          </div>
        ))}
      </dl>
    )}
    {(section.p2 || []).map((t) => (
      <p key={t.slice(0, 24)} className="text-[12.5px] leading-relaxed text-foreground/80">{t}</p>
    ))}
    {(section.tips || []).map((t) => (
      <p key={t.slice(0, 24)}
         className="rounded-lg border-l-2 border-primary/60 bg-primary/[0.06] px-3 py-2 text-[12px] leading-relaxed text-foreground/75">
        {t}
      </p>
    ))}
    {section.shot && (
      <figure className="overflow-hidden rounded-xl border border-border">
        <img src={`/help-shots/${section.shot}.jpg`} alt={section.caption || section.h}
             loading="lazy" data-testid={`help-shot-${section.shot}`} className="w-full"
             onError={(e) => { e.currentTarget.closest("figure").style.display = "none"; }} />
        {section.caption && (
          <figcaption className="border-t border-border bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground">
            {section.caption}
          </figcaption>
        )}
      </figure>
    )}
  </div>
);

const Editions = ({ items, mayIssue, onIssued }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  const download = async (edition) => {
    setBusy(edition.id);
    try {
      const res = await api.get("/help/handbook.pdf", {
        params: { edition: edition.id }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `servecor-user-handbook-v${edition.version}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const issue = async () => {
    setBusy("issue");
    try {
      await api.post("/help/editions", { note });
      toast.success("New edition issued");
      setOpen(false);
      setNote("");
      await onIssued();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  return (
    <Panel testId="panel-handbook-editions" kicker="Handbook" title="Editions issued"
           actions={mayIssue && (
             <Button size="sm" onClick={() => setOpen(true)} data-testid="issue-edition-button"
                     className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
               <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> Issue new edition
             </Button>
           )}>
      <div className="divide-y divide-border">
        {items.map((e) => (
          <div key={e.id} className="flex flex-wrap items-start gap-3 px-4 py-3"
               data-testid={`edition-row-${e.version}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[12px] font-semibold text-primary">
                  v{e.version}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  issued {dateTime(e.issued_at)} by {e.issued_by}
                </span>
              </div>
              <p className="mt-1 text-[12px] text-foreground/80">{e.note}</p>
              {e.changes?.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {e.changes.slice(0, 6).map((c) => (
                    <li key={c} className="text-[11px] text-muted-foreground">· {c}</li>
                  ))}
                  {e.changes.length > 6 && (
                    <li className="text-[11px] text-muted-foreground">
                      · and {e.changes.length - 6} more changes
                    </li>
                  )}
                </ul>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => download(e)}
                    disabled={busy === e.id} data-testid={`edition-download-${e.version}`}
                    className="h-8 rounded-full border-border px-3 text-[11px]">
              {busy === e.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <Download className="mr-1.5 h-3.5 w-3.5" />} PDF
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md border-border bg-card" data-testid="issue-edition-dialog">
          <DialogHeader>
            <DialogTitle>Issue a new handbook edition</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              A dated copy of the current text is kept, so staff can see what changed. Earlier
              editions remain downloadable exactly as issued.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={note} data-testid="edition-note-input"
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="What changed in this edition?"
                    className="bg-muted/40 text-[12.5px]" />
          <Button onClick={issue} disabled={busy === "issue" || note.trim().length < 5}
                  data-testid="edition-issue-confirm"
                  className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "issue" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Issue the edition
          </Button>
        </DialogContent>
      </Dialog>
    </Panel>
  );
};

export default function HelpGuide() {
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(params.get("chapter") || "");
  const [query, setQuery] = useState("");
  const [guide, setGuide] = useState(null);
  const [editions, setEditions] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get("/help/guide");
    setGuide(res.data);
    if (res.data.handbook_pdf_enabled) {
      try {
        const ed = await api.get("/help/editions");
        setEditions(ed.data);
      } catch { setEditions(null); }
    }
  }, []);

  useEffect(() => { load().catch(() => setGuide({ chapters: [] })); }, [load]);
  useEffect(() => {
    const wanted = params.get("chapter");
    if (wanted) setActive(wanted);
  }, [params]);

  const q = query.trim().toLowerCase();
  const chapters = useMemo(
    () => (guide?.chapters || []).filter(
      (c) => !q || JSON.stringify(c).toLowerCase().includes(q)), [guide, q]);
  const chapter = chapters.find((c) => c.id === active) || chapters[0];

  const pick = (id) => { setActive(id); setParams({ chapter: id }); };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/help/handbook.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `servecor-user-handbook-v${guide?.version || "1.0"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Handbook downloaded");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (!guide) return <Loading label="Loading the user manual" />;

  return (
    <div className="space-y-6" data-testid="help-guide-page">
      <div className="flex items-center gap-4">
        <span className="rounded-2xl bg-[#E2E8F0] p-2.5 ring-1 ring-black/5">
          <img src="/brand/servecor-lockup.png" alt="Servecor — Empowering Trust. Enabling Impact"
               data-testid="help-logo" className="h-24 w-auto" />
        </span>
      </div>
      <PageHeader
        eyebrow="Help · ServeCOR user manual"
        title="How to run ServeCOR, screen by screen"
        description="Written for the people who use this system daily. It covers only the areas your role works in, so what you read is what you can do. Search to jump straight to the screen in front of you."
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" data-testid="help-start-tour-button"
                    onClick={() => window.dispatchEvent(new CustomEvent("servecor:start-tour"))}
                    className="rounded-full border-border">
              <Compass className="mr-1.5 h-4 w-4 text-primary" /> Take the tour
            </Button>
            {guide.handbook_pdf_enabled && (
              <Button variant="outline" onClick={download} disabled={busy}
                      data-testid="help-download-button" className="rounded-full border-border">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  : <Download className="mr-1.5 h-4 w-4 text-primary" />} Download handbook
              </Button>
            )}
            <Button onClick={() => window.print()} data-testid="help-print-button"
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              <Printer className="mr-1.5 h-4 w-4" /> Print
            </Button>
          </div>
        )}
      />

      <div className="relative max-w-[420px]">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)}
               data-testid="help-search-input" placeholder="Search the manual…"
               className="h-9 rounded-full bg-muted/60 pl-9 text-[12.5px]" />
      </div>

      <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
        <nav className="h-fit space-y-0.5 rounded-xl border border-border bg-card p-2"
             data-testid="help-chapter-nav">
          {chapters.length === 0 && (
            <p className="px-2.5 py-3 text-[12px] text-muted-foreground" data-testid="help-no-results">
              Nothing in the manual matches “{query}”.
            </p>
          )}
          {chapters.map((c) => (
            <button key={c.id} type="button" onClick={() => pick(c.id)}
                    data-testid={`help-chapter-${c.id}`}
                    className={`flex w-full items-start gap-2 rounded-[10px] px-2.5 py-2 text-left text-[12.5px] transition-colors duration-150 ${
                      chapter?.id === c.id
                        ? "bg-primary font-semibold text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
              <BookOpen className="mt-[2px] h-3.5 w-3.5 shrink-0" />
              <span>{c.title}</span>
            </button>
          ))}
          <p className="px-2.5 py-3 text-[11px] leading-relaxed text-muted-foreground"
             data-testid="help-scope-note">
            {guide.chapters.length} chapters apply to your role.
            {!guide.handbook_pdf_enabled
              && " A tenant admin can switch on the printable PDF handbook at Settings → Tenant"
              + " policy → Help & training."}
          </p>
        </nav>

        {chapter && (
          <div className="space-y-4" data-testid="help-chapter-body">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="kicker">{chapter.kicker}</div>
              <h2 className="mt-1 font-display text-lg font-bold text-foreground"
                  data-testid="help-chapter-title">{chapter.title}</h2>
              <p className="mt-2 max-w-[70ch] text-[12.5px] leading-relaxed text-muted-foreground">
                {chapter.intro}
              </p>
            </div>
            {chapter.sections.map((s, i) => (
              <Panel key={s.h} testId={`help-section-${s.h.slice(0, 18).replace(/\W+/g, "-").toLowerCase()}`}
                     kicker={`Part ${chapter.title.split(" ·")[0]}.${i + 1}`} title={s.h}>
                <Body section={s} />
              </Panel>
            ))}
            {editions?.items?.length > 0 && (
              <Editions items={editions.items} mayIssue={editions.may_issue} onIssued={load} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
